import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../ui/Icon';
import { PageSkeleton } from '../ui/primitives';
import { useAdminToast } from './AdminShell';
import MagazynBoard from '../magazyn/MagazynBoard';
import { saveStockConfig, stockErrorText, useStockConfig, useStockItems, useStockOverview } from '../magazyn/api';
import ProductsTab from './magazyn/ProductsTab';
import OrderTagsTab from './magazyn/OrderTagsTab';
import { demandScopeText, resetInfo, useAdminStockPrint, useInvalidateStockConfig } from './magazyn/shared';

/*
 * Magazyn (panel admina): stan i dostawy (wspólna tablica z kioskiem), katalog produktów
 * i znaczniki zapasu przy zamówieniach. Wyłączony moduł → karta z opisem i „Włącz".
 */

type Tab = 'stock' | 'products' | 'orders';
const TABS: Tab[] = ['stock', 'products', 'orders'];

/* ---------- moduł wyłączony ---------- */
function DisabledHero({ onSettings }: { onSettings: () => void }) {
  const push = useAdminToast();
  const invalidate = useInvalidateStockConfig();
  const [busy, setBusy] = useState(false);

  async function enable() {
    setBusy(true);
    try {
      await saveStockConfig({ enabled: true });
      invalidate();
      push('Magazyn włączony');
    } catch (e) {
      push(stockErrorText(e, 'Nie udało się włączyć Magazynu'), 'err');
    } finally {
      setBusy(false);
    }
  }

  const steps: [string, string, string][] = [
    ['sunrise', 'Rano dostawa', 'Wpisujesz, ile przyszło z piekarni — od razu widać, czego brakuje do zamówień.'],
    ['clipboard-list', 'Lista „Do dobrania”', 'Jednym kliknięciem drukujesz brakujące produkty i schodzisz z nią do piekarni.'],
    ['clipboard-check', 'Rozliczenie', 'Lista jest już wypełniona — odznaczasz tylko to, czego nie udało się wziąć.'],
  ];

  return (
    <div className="card mga-hero">
      <div className="mga-hero-main">
        <span className="mga-hero-badge"><Icon name="warehouse" size={14} /> Moduł opcjonalny</span>
        <h2>Magazyn pieczywa przy pakowaniu</h2>
        <p className="mga-hero-lead">
          Koniec z liczeniem produktów z raportu o 11:00. PackFlow wie, ile pieczywa jest na stanowisku i ile potrzebują zamówienia w kolejce.
        </p>
        <ol className="mga-steps">
          {steps.map(([ic, t, d], i) => (
            <li key={t}>
              <span className="mga-step-n">{i + 1}</span>
              <div>
                <div className="t"><Icon name={ic} size={15} /> {t}</div>
                <div className="d">{d}</div>
              </div>
            </li>
          ))}
        </ol>
        <ul className="mga-perks">
          <li><Icon name="package-check" size={15} /> Spakowane zamówienie samo zdejmuje produkty ze stanu</li>
          <li><Icon name="tag" size={15} /> Przy zamówieniach znacznik: zielony — jest na stanie, czerwony — brakuje</li>
          <li><Icon name="rotate-ccw" size={15} /> Codzienny reset o wybranej godzinie (domyślnie o północy)</li>
        </ul>
        <div className="mga-hero-actions">
          <button className="btn btn-primary" onClick={enable} disabled={busy}><Icon name="power" size={16} /> Włącz Magazyn</button>
          <button className="btn btn-ghost" onClick={onSettings}><Icon name="sliders-horizontal" size={16} /> Najpierw ustawienia</button>
        </div>
      </div>
      <div className="mga-hero-art" aria-hidden>
        <div className="mga-mock">
          <div className="mga-mock-head"><Icon name="clipboard-list" size={14} /> Do dobrania <span className="mga-mock-time">09:42</span></div>
          {([['wheat', 'Chleb żytni 700 g', 6], ['croissant', 'Bułka grahamka', 12], ['wheat', 'Shokupan', 3], ['cake-slice', 'Sernik nowojorski', 1]] as [string, string, number][]).map(([ic, n, q]) => (
            <div className="mga-mock-row" key={n}>
              <span className="mga-mock-ic"><Icon name={ic} size={14} /></span>
              <span className="mga-mock-name">{n}</span>
              <b className="num">{q}</b>
            </div>
          ))}
          <div className="mga-mock-tags">
            <span className="pill pill-ok"><Icon name="circle-check" size={12} /> Na stanie</span>
            <span className="pill pill-warn"><Icon name="circle-alert" size={12} /> Częściowo</span>
            <span className="pill pill-err"><Icon name="circle-x" size={12} /> Brak</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- strona ---------- */
export default function MagazynPage() {
  const navigate = useNavigate();
  const push = useAdminToast();
  const onPrint = useAdminStockPrint(push);
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') as Tab | null;
  const tab: Tab = raw && TABS.indexOf(raw) >= 0 ? raw : 'stock';
  const setTab = (t: Tab) => setParams(t === 'stock' ? {} : { tab: t }, { replace: true });

  const { data: config, isLoading } = useStockConfig();
  const enabled = !!config?.enabled;
  const { data: ov } = useStockOverview({ enabled });
  const { data: catalog } = useStockItems({ enabled });

  const openSettings = () => navigate('/settings?tab=magazyn');

  if (isLoading) return <PageSkeleton />;

  const tracked = catalog ? catalog.items.filter((i) => i.tracked).length : undefined;
  const attention = ov ? ov.totals.ordersShort + ov.totals.ordersPartial : 0;
  const sub = enabled ? [resetInfo(ov ?? config), demandScopeText(ov?.demandScope ?? config?.demandScope)].filter(Boolean).join(' · ') : 'Moduł wyłączony';

  return (
    <div className="page mga-page">
      <div className="page-head">
        <div>
          <h1>Magazyn</h1>
          <div className="ph-sub">{sub}</div>
        </div>
        <div className="actions">
          <button className="btn btn-ghost" onClick={openSettings}><Icon name="settings" size={16} /> Ustawienia</button>
        </div>
      </div>

      {!enabled ? (
        <DisabledHero onSettings={openSettings} />
      ) : (
        <>
          <div className="tabs">
            <button className={`tab ${tab === 'stock' ? 'active' : ''}`} onClick={() => setTab('stock')}>
              <Icon name="warehouse" size={15} /> Stan i dostawy
              {ov && ov.totals.toTake > 0 && <span className="cnt mga-cnt-warn" title="Sztuk do dobrania">{ov.totals.toTake}</span>}
            </button>
            <button className={`tab ${tab === 'products' ? 'active' : ''}`} onClick={() => setTab('products')}>
              <Icon name="wheat" size={15} /> Produkty
              {tracked !== undefined && <span className="cnt">{tracked}</span>}
            </button>
            <button className={`tab ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>
              <Icon name="tags" size={15} /> Zamówienia
              {attention > 0 && <span className="cnt mga-cnt-err" title="Zamówienia bez pełnego zapasu">{attention}</span>}
            </button>
          </div>

          {tab === 'stock' && (
            <MagazynBoard variant="admin" allowPdf onPrint={onPrint} notify={push} />
          )}
          {tab === 'products' && <ProductsTab />}
          {tab === 'orders' && <OrderTagsTab />}
        </>
      )}
    </div>
  );
}
